import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    CheckCircle,
    AlertCircle,
    Search,
    Edit2,
    Trash2,
    ChevronRight,
    ChevronDown,
    Plus,
    ChevronsUpDown,
    Upload,
    X,
    Loader2,
    RefreshCw
} from 'lucide-react';
import InnerHeader from '../components/common/InnerHeader/InnerHeader';
import InnerFooter from '../components/common/InnerFooter/InnerFooter';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal/ConfirmDeleteModal';
import PoleDesignSidebar from '../components/common/PoleDesignSidebar/PoleDesignSidebar';
import defaultThumb from '../assets/thumb.png';
import './Dashboard.css';
import './DesignComponents.css';
import './styles/pages-common.css';
import { usePageLoader } from '../components/common/PageLoader/PageLoader';

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');

const DESIGN_STATUS_OPTIONS = [
    'Concept',
    'Operational',
];

// Helper to extract clear, human-readable error messages from API responses
const extractApiErrorMessage = (errData, fallback = 'Operation failed. Please try again.') => {
    if (!errData) return fallback;
    if (typeof errData === 'string') {
        const str = errData.trim();
        if (str.toLowerCase().includes('unique') || str.toLowerCase().includes('already exists') || str.toLowerCase().includes('duplicate')) {
            return "This component is already added to this Pole Design's BOM. Please edit the existing item to change its quantity.";
        }
        return str || fallback;
    }

    // Check direct string fields
    if (typeof errData.message === 'string' && errData.message.trim()) {
        const msg = errData.message.trim();
        if (msg.toLowerCase().includes('unique') || msg.toLowerCase().includes('already exists') || msg.toLowerCase().includes('duplicate')) {
            return "This component is already added to this Pole Design's BOM. Please edit the existing item to change its quantity.";
        }
        return msg;
    }
    if (typeof errData.detail === 'string' && errData.detail.trim()) {
        const msg = errData.detail.trim();
        if (msg.toLowerCase().includes('unique') || msg.toLowerCase().includes('already exists') || msg.toLowerCase().includes('duplicate')) {
            return "This component is already added to this Pole Design's BOM. Please edit the existing item to change its quantity.";
        }
        return msg;
    }
    if (typeof errData.error === 'string' && errData.error.trim()) {
        const msg = errData.error.trim();
        if (msg.toLowerCase().includes('unique') || msg.toLowerCase().includes('already exists') || msg.toLowerCase().includes('duplicate')) {
            return "This component is already added to this Pole Design's BOM. Please edit the existing item to change its quantity.";
        }
        return msg;
    }

    // non_field_errors array (Django REST Framework duplicate / unique_together errors)
    if (Array.isArray(errData.non_field_errors) && errData.non_field_errors.length > 0) {
        const joined = errData.non_field_errors.join(' ');
        if (joined.toLowerCase().includes('unique') || joined.toLowerCase().includes('already exists') || joined.toLowerCase().includes('duplicate')) {
            return "This component is already added to this Pole Design's BOM. Please edit the existing item to change its quantity.";
        }
        return joined;
    }

    // detail array (FastAPI / Pydantic)
    if (Array.isArray(errData.detail) && errData.detail.length > 0) {
        const details = errData.detail.map((d) => {
            if (typeof d === 'string') return d;
            if (d && typeof d === 'object') {
                const loc = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : '';
                return loc ? `${loc}: ${d.msg || d.message}` : (d.msg || d.message || JSON.stringify(d));
            }
            return String(d);
        });
        const fullDetail = details.join(' | ');
        if (fullDetail.toLowerCase().includes('unique') || fullDetail.toLowerCase().includes('already exists') || fullDetail.toLowerCase().includes('duplicate')) {
            return "This component is already added to this Pole Design's BOM. Please edit the existing item to change its quantity.";
        }
        return fullDetail;
    }

    // Field-level error dictionaries (e.g. { component_id: ["..."], pole_design_id: ["..."] } or { errors: { ... } })
    const targetObj = (errData.errors && typeof errData.errors === 'object') ? errData.errors : errData;
    if (typeof targetObj === 'object' && targetObj !== null) {
        const fieldErrors = [];
        for (const [key, val] of Object.entries(targetObj)) {
            if (['status', 'statusCode', 'code', 'success'].includes(key)) continue;
            let valStr = '';
            if (Array.isArray(val)) {
                valStr = val.join(', ');
            } else if (typeof val === 'string') {
                valStr = val;
            } else if (typeof val === 'object' && val !== null) {
                valStr = val.message || val.detail || val.msg || JSON.stringify(val);
            }
            if (valStr) {
                if (valStr.toLowerCase().includes('unique') || valStr.toLowerCase().includes('already exists') || valStr.toLowerCase().includes('duplicate')) {
                    return "This component is already added to this Pole Design's BOM. Please edit the existing item to change its quantity.";
                }
                const readableKey = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                fieldErrors.push(`${readableKey}: ${valStr}`);
            }
        }
        if (fieldErrors.length > 0) {
            return fieldErrors.join(' | ');
        }
    }

    return fallback;
};

// Helper to resolve media image URLs from API response
const getImageUrl = (imagePath) => {
    if (!imagePath || typeof imagePath !== 'string') return null;
    const trimmed = imagePath.trim();
    if (!trimmed) return null;
    if (
        trimmed.startsWith('http://') ||
        trimmed.startsWith('https://') ||
        trimmed.startsWith('data:') ||
        trimmed.startsWith('blob:')
    ) {
        return trimmed;
    }
    const serverOrigin = (API_BASE_URL || '').replace(/\/api\/?$/, '');
    const cleanPath = trimmed.startsWith('/') ? trimmed.slice(1) : trimmed;
    if (cleanPath.startsWith('media/')) {
        return `${serverOrigin}/${cleanPath}`;
    }
    return `${serverOrigin}/media/${cleanPath}`;
};

// ─── Pole Thumbnail Renderer (with image support & thumb.png fallback) ────────
const PoleThumbnail = ({ code, id, thumbnail }) => {
    const [imgError, setImgError] = useState(false);

    useEffect(() => {
        setImgError(false);
    }, [thumbnail]);

    const resolvedUrl = getImageUrl(thumbnail);
    const displaySrc = (!imgError && resolvedUrl) ? resolvedUrl : defaultThumb;

    return (
        <div className="pole-thumb-wrap" title={`Pole Design: ${code || id}`}>
            <img
                src={displaySrc}
                alt={code || 'Pole Design'}
                className="pole-thumb-svg"
                style={{ maxHeight: '80px', width: 'auto', objectFit: 'contain' }}
                onError={() => {
                    if (!imgError && resolvedUrl) {
                        setImgError(true);
                    }
                }}
            />
        </div>
    );
};

const ROWS_PER_PAGE = 10;

const EMPTY_FORM = {
    code: '',
    name: '',
    description: '',
    design_status: 'Concept',
    thumbnail: '',
    imageFile: null,
    status: true,
};

export const PoleDesigns = ({ setView }) => {
    const [user, setUser] = useState(() => {
        try {
            const stored = localStorage.getItem('user');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });

    const [alert, setAlert] = useState(null);
    const [poleDesigns, setPoleDesigns] = useState([]);
    const [availableComponents, setAvailableComponents] = useState([]);
    const [boms, setBoms] = useState({}); // { [pole_design_id]: [bomItem, ...] }
    const [loadingBoms, setLoadingBoms] = useState({}); // { [pole_design_id]: boolean }

    const [isLoading, setIsLoading] = useState(true);
    const { setPageLoading } = usePageLoader();

    useEffect(() => {
        setPageLoading(isLoading);
        return () => setPageLoading(false);
    }, [isLoading, setPageLoading]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmittingBOM, setIsSubmittingBOM] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState('id');
    const [sortDir, setSortDir] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRows, setExpandedRows] = useState({});
    const [activeTopNav, setActiveTopNav] = useState('Pole Design');

    // ── Form view state (Add / Edit Pole Design) ──────────────────────────────
    const [showForm, setShowForm] = useState(false);
    const [editingDesign, setEditingDesign] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [pendingDelete, setPendingDelete] = useState(null);

    // ── BOM Modal State (Add / Edit Component in BOM) ─────────────────────────
    const [bomModal, setBomModal] = useState({
        isOpen: false,
        poleId: null,
        poleName: '',
        poleCode: '',
        isEdit: false,
        bomItemId: null,
    });

    const [bomFormData, setBomFormData] = useState({
        component_id: '',
        code: '',
        name: '',
        category: '',
        unitOfMeasure: 'pcs',
        quantity: 1,
        notes: '',
        status: true,
    });
    const [bomFormErrors, setBomFormErrors] = useState({});
    const [pendingDeleteBOM, setPendingDeleteBOM] = useState(null);

    // ── Fetch user details ────────────────────────────────────────────────────
    const fetchUserDetails = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return null;
            const res = await fetch(`${API_BASE_URL}/user/AlluserDetails/`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) throw new Error('Failed');
            const data = await res.json();
            if (data.status && data.data?.user) {
                const u = data.data.user;
                if (u.profile_picture && !u.picture) u.picture = u.profile_picture;
                setUser(u);
                localStorage.setItem('user', JSON.stringify(u));
                return u;
            }
            throw new Error('Invalid');
        } catch {
            const stored = localStorage.getItem('user');
            if (stored) {
                const u = JSON.parse(stored);
                setUser(u);
                return u;
            }
            return null;
        }
    };

    // ── Fetch Components List (GET /api/components/) for BOM dropdown ────────
    const fetchComponents = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;
            const res = await fetch(`${API_BASE_URL}/components/`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });
            if (!res.ok) return;
            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.results)) list = data.results;

            if (list.length > 0) {
                setAvailableComponents(
                    list.map((c) => ({
                        id: c.component_id ?? c.id,
                        code: c.component_code || c.code || '',
                        name: c.component_name || c.name || '',
                        category_id: c.category_id,
                        category: c.category_name || c.category || '',
                        unitOfMeasure: c.unit_of_measure || c.unitOfMeasure || 'pcs',
                        description: c.component_description || c.description || '',
                    }))
                );
            }
        } catch (e) {
            console.warn('[PoleDesigns] Error fetching components:', e.message);
        }
    };

    // ── Fetch Pole Designs List (GET /api/pole-designs/) ──────────────────────
    const fetchPoleDesigns = async () => {
        setIsLoading(true);
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                setIsLoading(false);
                return;
            }

            const res = await fetch(`${API_BASE_URL}/pole-designs/`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!res.ok) {
                if (res.status === 401) {
                    localStorage.removeItem('accessToken');
                    if (setView) setView('signin');
                    return;
                }
                throw new Error(`Failed to fetch pole designs (${res.status})`);
            }

            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.results)) list = data.results;

            const mapped = list.map((item) => ({
                id: item.pole_design_id ?? item.id,
                code: item.design_code ?? item.code ?? '',
                name: item.design_name ?? item.name ?? '',
                description: item.description ?? '',
                design_status: item.design_status ?? 'Concept',
                image: item.image ?? item.thumbnail ?? '',
                status: item.status !== false,
                created_utc: item.created_utc,
                created_by: item.created_by,
                last_update_date: item.last_update_date,
                last_updated_by: item.last_updated_by,
                components: Array.isArray(item.components) ? item.components : [],
            }));

            setPoleDesigns(mapped);

            // Fetch BOM for each pole design in the background to calculate counts
            mapped.forEach((p) => {
                if (p.id) {
                    fetchBOMForPole(p.id, false);
                }
            });
        } catch (error) {
            console.error('[PoleDesigns] Error fetching pole designs:', error);
            setAlert({ type: 'error', message: error.message || 'Failed to load pole designs.' });
            setTimeout(() => setAlert(null), 4000);
            setPoleDesigns([]);
        } finally {
            setIsLoading(false);
        }
    };

    // ── Fetch BOM for a specific pole (GET /api/pole-designs/{id}/bom/) ─────────
    const fetchBOMForPole = async (poleId, showLoader = true) => {
        if (showLoader) {
            setLoadingBoms((prev) => ({ ...prev, [poleId]: true }));
        }
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;

            const res = await fetch(`${API_BASE_URL}/pole-designs/${poleId}/bom/`, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!res.ok) {
                throw new Error(`Failed to fetch BOM (${res.status})`);
            }

            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) list = data;
            else if (data && data.status && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.data)) list = data.data;
            else if (data && Array.isArray(data.results)) list = data.results;

            const mapped = list.map((b) => ({
                bom_item_id: b.bom_item_id ?? b.id,
                pole_design_id: b.pole_design_id ?? poleId,
                component_id: b.component_id,
                code: b.component_code ?? '',
                name: b.component_name ?? '',
                unitOfMeasure: b.unit_of_measure ?? 'pcs',
                category_id: b.category_id,
                category: b.category_name ?? '',
                quantity: parseFloat(b.quantity) || 1,
                notes: b.notes ?? '',
                created_utc: b.created_utc,
                created_by: b.created_by,
                last_update_date: b.last_update_date,
                last_updated_by: b.last_updated_by,
                status: b.status !== false,
            }));

            setBoms((prev) => ({ ...prev, [poleId]: mapped }));
        } catch (err) {
            console.error(`[PoleDesigns] Error fetching BOM for pole ${poleId}:`, err);
        } finally {
            if (showLoader) {
                setLoadingBoms((prev) => ({ ...prev, [poleId]: false }));
            }
        }
    };

    // ── Initial Load ──────────────────────────────────────────────────────────
    useEffect(() => {
        let isMounted = true;
        fetchUserDetails().then((userData) => {
            if (!userData && isMounted && setView) {
                setView('signin');
            }
        });
        fetchComponents();
        fetchPoleDesigns();

        return () => {
            isMounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // ── Calculate total components count for a pole ───────────────────────────
    const getComponentCount = (item) => {
        if (!item) return 0;
        const poleBom = boms[item.id];
        if (Array.isArray(poleBom)) {
            return poleBom.reduce((sum, c) => sum + (Number(c.quantity) || 1), 0);
        }
        if (Array.isArray(item.components) && item.components.length > 0) {
            return item.components.reduce((sum, c) => sum + (Number(c.quantity) || 1), 0);
        }
        return 0;
    };

    // ── Search & Sorting Logic ────────────────────────────────────────────────
    const filtered = (poleDesigns || []).filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        const poleBom = boms[item.id] || item.components || [];
        const matchesComp = poleBom.some(
            (c) =>
                (c.code || '').toLowerCase().includes(q) ||
                (c.name || '').toLowerCase().includes(q) ||
                (c.category || '').toLowerCase().includes(q)
        );
        return (
            (item.code || '').toLowerCase().includes(q) ||
            (item.name || '').toLowerCase().includes(q) ||
            (item.description || '').toLowerCase().includes(q) ||
            (item.design_status || '').toLowerCase().includes(q) ||
            matchesComp
        );
    });

    const sorted = [...filtered].sort((a, b) => {
        let av = a[sortField];
        let bv = b[sortField];
        if (sortField === 'components' || sortField === 'count') {
            av = getComponentCount(a);
            bv = getComponentCount(b);
        }
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    const totalPages = Math.max(1, Math.ceil(sorted.length / ROWS_PER_PAGE));
    const paginated = sorted.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const toggleRowExpand = (id) => {
        const nextState = !expandedRows[id];
        setExpandedRows((prev) => ({
            ...prev,
            [id]: nextState,
        }));
        if (nextState && !boms[id]) {
            fetchBOMForPole(id, true);
        }
    };

    // ── Toggle Status (PATCH /api/pole-designs/{id}/status/) ──────────────────
    const handleToggleStatus = async (id, e) => {
        if (e) e.stopPropagation();
        const targetDesign = poleDesigns.find((c) => c.id === id);
        if (!targetDesign) return;

        const nextStatus = !targetDesign.status;
        const accessToken = localStorage.getItem('accessToken');

        // Optimistic UI update
        setPoleDesigns((prev) =>
            prev.map((c) => (c.id === id ? { ...c, status: nextStatus } : c))
        );

        try {
            const res = await fetch(`${API_BASE_URL}/pole-designs/${id}/status/`, {
                method: 'PATCH',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status: nextStatus }),
            });

            if (!res.ok) {
                throw new Error(`Failed to update status (${res.status})`);
            }

            setAlert({
                type: 'success',
                message: `Pole Design "${targetDesign.name || targetDesign.code}" status updated to ${nextStatus ? 'Active' : 'Inactive'
                    }.`,
            });
            setTimeout(() => setAlert(null), 3000);
        } catch (error) {
            console.error('[PoleDesigns] Status update error:', error);
            // Revert state on error
            setPoleDesigns((prev) =>
                prev.map((c) => (c.id === id ? { ...c, status: targetDesign.status } : c))
            );
            setAlert({ type: 'error', message: 'Failed to update pole design status on server.' });
            setTimeout(() => setAlert(null), 3000);
        }
    };

    // ── Handle Thumbnail File Upload ──────────────────────────────────────────
    const handleThumbnailFileChange = (e) => {
        const file = e.target.files && e.target.files[0];
        if (file) {
            const validTypes = ['image/png', 'image/jpeg', 'image/jpg'];
            if (!validTypes.includes(file.type)) {
                setAlert({ type: 'error', message: 'Please upload only .png or .jpg / .jpeg images.' });
                setTimeout(() => setAlert(null), 3000);
                return;
            }
            if (file.size > 5 * 1024 * 1024) {
                setAlert({ type: 'error', message: 'Thumbnail file size must be less than 5MB.' });
                setTimeout(() => setAlert(null), 3000);
                return;
            }
            const reader = new FileReader();
            reader.onload = (event) => {
                setFormData((prev) => ({
                    ...prev,
                    thumbnail: event.target.result,
                    imageFile: file,
                }));
            };
            reader.readAsDataURL(file);
        }
    };

    // ── Open Add Pole Design Form ─────────────────────────────────────────────
    const handleOpenAdd = () => {
        setEditingDesign(null);
        setFormData({ ...EMPTY_FORM, status: true });
        setFormErrors({});
        setShowForm(true);
    };

    // ── Open Edit Pole Design Form (GET /api/pole-designs/{id}) ───────────────
    const handleOpenEdit = async (design) => {
        setEditingDesign(design);
        setFormData({
            code: design.code || '',
            name: design.name || '',
            description: design.description || '',
            design_status: design.design_status || 'Concept',
            thumbnail: design.image || design.thumbnail || '',
            imageFile: null,
            status: design.status ?? true,
        });
        setFormErrors({});
        setShowForm(true);

        // Fetch fresh single-record data from API
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (accessToken && design.id) {
                const res = await fetch(`${API_BASE_URL}/pole-designs/${design.id}`, {
                    method: 'GET',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                });
                if (res.ok) {
                    const singleData = await res.json();
                    const record = singleData.data || singleData;
                    if (record) {
                        setFormData((prev) => ({
                            ...prev,
                            code: record.design_code ?? prev.code,
                            name: record.design_name ?? prev.name,
                            description: record.description ?? prev.description,
                            design_status: record.design_status ?? prev.design_status,
                            thumbnail: record.image ?? prev.thumbnail,
                            status: record.status !== undefined ? Boolean(record.status) : prev.status,
                        }));
                    }
                }
            }
        } catch (err) {
            console.error('[PoleDesigns] Failed to fetch single pole design:', err);
        }
    };

    // ── Delete Pole Design (DELETE /api/pole-designs/{id}/delete/) ─────────────
    const handleConfirmDelete = async () => {
        if (!pendingDelete) return;
        const targetId = pendingDelete.id || pendingDelete;
        const accessToken = localStorage.getItem('accessToken');
        setIsDeleting(true);

        try {
            const res = await fetch(`${API_BASE_URL}/pole-designs/${targetId}/delete/`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(extractApiErrorMessage(errData, `Delete failed with status ${res.status}`));
            }

            setPoleDesigns((prev) => prev.filter((c) => c.id !== targetId));
            setAlert({ type: 'success', message: 'Pole Design deleted successfully.' });
            setTimeout(() => setAlert(null), 3000);
            setPendingDelete(null);
        } catch (error) {
            console.error('[PoleDesigns] Delete error:', error);
            setAlert({ type: 'error', message: error.message || 'Failed to delete pole design.' });
            setTimeout(() => setAlert(null), 3000);
        } finally {
            setIsDeleting(false);
        }
    };

    // ── Form Input Handler ────────────────────────────────────────────────────
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    // ── Save Pole Design (POST /api/pole-designs/create/ OR PUT /api/pole-designs/{id}/update/) ──
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const errors = {};
        if (!formData.code.trim()) errors.code = 'Design Code is required.';
        if (!formData.name.trim()) errors.name = 'Pole Design Name is required.';
        if (!formData.design_status || !formData.design_status.trim()) {
            errors.design_status = 'Design Status is required.';
        }

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        const accessToken = localStorage.getItem('accessToken');
        setIsSubmitting(true);

        const fd = new FormData();
        fd.append('design_code', formData.code.trim());
        fd.append('design_name', formData.name.trim());
        fd.append('description', formData.description.trim());
        fd.append('design_status', (formData.design_status || 'Concept').trim());
        // Explicitly send status=true on create / formData.status on update
        fd.append('status', editingDesign ? (formData.status !== false ? 'true' : 'false') : 'true');
        if (formData.imageFile instanceof File) {
            fd.append('image', formData.imageFile);
        }

        try {
            if (editingDesign) {
                // ── PUT /api/pole-designs/{id}/update/ ──
                const res = await fetch(`${API_BASE_URL}/pole-designs/${editingDesign.id}/update/`, {
                    method: 'PUT',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: fd,
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    if (errData && typeof errData === 'object') {
                        const targetErrors = errData.errors || errData;
                        const fieldMap = {};
                        if (targetErrors.design_name) {
                            fieldMap.name = Array.isArray(targetErrors.design_name) ? targetErrors.design_name.join(' ') : String(targetErrors.design_name);
                        }
                        if (targetErrors.design_code) {
                            fieldMap.code = Array.isArray(targetErrors.design_code) ? targetErrors.design_code.join(' ') : String(targetErrors.design_code);
                        }
                        if (targetErrors.design_status) {
                            fieldMap.design_status = Array.isArray(targetErrors.design_status) ? targetErrors.design_status.join(' ') : String(targetErrors.design_status);
                        }
                        if (Object.keys(fieldMap).length > 0) {
                            setFormErrors((prev) => ({ ...prev, ...fieldMap }));
                        }
                    }
                    throw new Error(extractApiErrorMessage(errData, `Update failed (${res.status})`));
                }

                setAlert({ type: 'success', message: `Pole Design "${formData.name}" updated successfully!` });
            } else {
                // ── POST /api/pole-designs/create/ ──
                const res = await fetch(`${API_BASE_URL}/pole-designs/create/`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: fd,
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    if (errData && typeof errData === 'object') {
                        const targetErrors = errData.errors || errData;
                        const fieldMap = {};
                        if (targetErrors.design_name) {
                            fieldMap.name = Array.isArray(targetErrors.design_name) ? targetErrors.design_name.join(' ') : String(targetErrors.design_name);
                        }
                        if (targetErrors.design_code) {
                            fieldMap.code = Array.isArray(targetErrors.design_code) ? targetErrors.design_code.join(' ') : String(targetErrors.design_code);
                        }
                        if (targetErrors.design_status) {
                            fieldMap.design_status = Array.isArray(targetErrors.design_status) ? targetErrors.design_status.join(' ') : String(targetErrors.design_status);
                        }
                        if (Object.keys(fieldMap).length > 0) {
                            setFormErrors((prev) => ({ ...prev, ...fieldMap }));
                        }
                    }
                    throw new Error(extractApiErrorMessage(errData, `Creation failed (${res.status})`));
                }

                setAlert({ type: 'success', message: `Pole Design "${formData.name}" created successfully!` });
            }

            setTimeout(() => setAlert(null), 3000);
            setShowForm(false);
            setEditingDesign(null);
            setFormData(EMPTY_FORM);
            await fetchPoleDesigns();
        } catch (error) {
            console.error('[PoleDesigns] Form submit error:', error);
            setFormErrors((prev) => ({ ...prev, submit: error.message || 'Operation failed. Please try again.' }));
            setAlert({ type: 'error', message: error.message || 'Error saving pole design.' });
            setTimeout(() => setAlert(null), 4000);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── BOM Modal Openers ─────────────────────────────────────────────────────
    const handleOpenAddBOMItem = (pole) => {
        setBomFormData({
            component_id: '',
            code: '',
            name: '',
            category: '',
            unitOfMeasure: 'pcs',
            quantity: 1,
            notes: '',
            status: true,
        });
        setBomFormErrors({});
        setBomModal({
            isOpen: true,
            poleId: pole.id,
            poleName: pole.name,
            poleCode: pole.code,
            isEdit: false,
            bomItemId: null,
        });
    };

    const handleOpenEditBOMItem = (pole, comp) => {
        setBomFormData({
            component_id: comp.component_id ? String(comp.component_id) : '',
            code: comp.code || '',
            name: comp.name || '',
            category: comp.category || '',
            unitOfMeasure: comp.unitOfMeasure || 'pcs',
            quantity: comp.quantity || 1,
            notes: comp.notes || '',
            status: comp.status ?? true,
        });
        setBomFormErrors({});
        setBomModal({
            isOpen: true,
            poleId: pole.id,
            poleName: pole.name,
            poleCode: pole.code,
            isEdit: true,
            bomItemId: comp.bom_item_id,
        });
    };

    // ── BOM Component Select Change ───────────────────────────────────────────
    const handleBOMComponentSelect = (selectedId) => {
        const found = availableComponents.find((c) => String(c.id) === String(selectedId) || c.code === selectedId);
        if (found) {
            const alreadyInBOM =
                !bomModal.isEdit &&
                boms[bomModal.poleId]?.some((b) => String(b.component_id) === String(found.id));

            setBomFormData((prev) => ({
                ...prev,
                component_id: String(found.id),
                code: found.code,
                name: found.name,
                category: found.category,
                unitOfMeasure: found.unitOfMeasure || 'pcs',
            }));

            if (alreadyInBOM) {
                setBomFormErrors((prev) => ({
                    ...prev,
                    component: `"${found.code} - ${found.name}" is already added to this Pole Design's BOM. Please choose another component or edit the existing item.`,
                    submit: '',
                }));
            } else {
                setBomFormErrors((prev) => ({ ...prev, component: '', submit: '' }));
            }
        } else {
            setBomFormData((prev) => ({
                ...prev,
                component_id: selectedId,
                code: '',
                name: '',
                category: '',
                unitOfMeasure: 'pcs',
            }));
            if (bomFormErrors.component) {
                setBomFormErrors((prev) => ({ ...prev, component: '', submit: '' }));
            }
        }
    };

    // ── Save Component to BOM (POST /api/pole-designs/{id}/bom/create/ OR PUT /api/pole-design-bom/{id}/update/) ──
    const handleSaveBOMItem = async (e) => {
        e.preventDefault();
        const errors = {};
        if (!bomFormData.component_id) {
            errors.component = 'Please select a Component.';
        } else if (!bomModal.isEdit) {
            const alreadyInBOM = boms[bomModal.poleId]?.some(
                (b) => String(b.component_id) === String(bomFormData.component_id)
            );
            if (alreadyInBOM) {
                const compName = bomFormData.name || bomFormData.code || 'This component';
                errors.component = `"${compName}" is already added to this Pole Design's BOM. Please edit the existing item to adjust its quantity.`;
            }
        }

        if (!bomFormData.quantity || Number(bomFormData.quantity) < 1) {
            errors.quantity = 'Quantity must be at least 1.';
        }

        if (Object.keys(errors).length > 0) {
            setBomFormErrors(errors);
            return;
        }

        const accessToken = localStorage.getItem('accessToken');
        setIsSubmittingBOM(true);

        const payload = {
            component_id: parseInt(bomFormData.component_id, 10),
            quantity: Number(bomFormData.quantity),
            notes: bomFormData.notes ? bomFormData.notes.trim() : '',
        };

        try {
            if (bomModal.isEdit) {
                // ── PUT /api/pole-design-bom/{bom_item_id}/update/ ──
                const res = await fetch(`${API_BASE_URL}/pole-design-bom/${bomModal.bomItemId}/update/`, {
                    method: 'PUT',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    throw new Error(extractApiErrorMessage(errData, `Update failed (${res.status})`));
                }

                setAlert({
                    type: 'success',
                    message: `BOM component "${bomFormData.code} - ${bomFormData.name}" updated successfully.`,
                });
            } else {
                // ── POST /api/pole-designs/{pole_design_id}/bom/create/ ──
                const res = await fetch(`${API_BASE_URL}/pole-designs/${bomModal.poleId}/bom/create/`, {
                    method: 'POST',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    throw new Error(extractApiErrorMessage(errData, `Creation failed (${res.status})`));
                }

                setAlert({
                    type: 'success',
                    message: `BOM component "${bomFormData.code} - ${bomFormData.name}" added successfully.`,
                });
            }

            setTimeout(() => setAlert(null), 3000);
            const targetPoleId = bomModal.poleId;
            setBomModal({
                isOpen: false,
                poleId: null,
                poleName: '',
                poleCode: '',
                isEdit: false,
                bomItemId: null,
            });

            if (targetPoleId) {
                await fetchBOMForPole(targetPoleId, false);
            }
        } catch (error) {
            console.error('[PoleDesigns] Save BOM item error:', error);
            const errMsg = error.message || 'Error saving BOM component.';
            setBomFormErrors((prev) => ({ ...prev, submit: errMsg }));
            setAlert({ type: 'error', message: errMsg });
            setTimeout(() => setAlert(null), 4000);
        } finally {
            setIsSubmittingBOM(false);
        }
    };

    // ── Delete Component from BOM (DELETE /api/pole-design-bom/{bom_item_id}/delete/) ──
    const handleConfirmDeleteBOM = async () => {
        if (!pendingDeleteBOM) return;
        const { poleId, bomItemId, compName } = pendingDeleteBOM;
        const accessToken = localStorage.getItem('accessToken');
        setIsDeleting(true);

        try {
            const res = await fetch(`${API_BASE_URL}/pole-design-bom/${bomItemId}/delete/`, {
                method: 'DELETE',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(extractApiErrorMessage(errData, `Delete failed with status ${res.status}`));
            }

            setAlert({ type: 'success', message: `Component "${compName}" removed from BOM.` });
            setTimeout(() => setAlert(null), 3000);
            setPendingDeleteBOM(null);

            if (poleId) {
                await fetchBOMForPole(poleId, false);
            }
        } catch (error) {
            console.error('[PoleDesigns] Delete BOM item error:', error);
            setAlert({ type: 'error', message: error.message || 'Failed to remove component from BOM.' });
            setTimeout(() => setAlert(null), 3000);
        } finally {
            setIsDeleting(false);
        }
    };

    const isEditMode = Boolean(editingDesign);

    // Helper for status badge CSS class
    const getStatusClass = (statusStr) => {
        if (!statusStr) return 'concept';
        const s = statusStr.toLowerCase().replace(/\s+/g, '-');
        if (s.includes('operation')) return 'operational';
        return 'concept';
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="assets-root"
        >
            {/* ══ 1. MAIN HEADER ══════════════════════════════════════════════════ */}
            <InnerHeader
                user={user}
                setView={setView}
                onProfilePhotoSuccess={async () => {
                    setTimeout(async () => {
                        await fetchUserDetails();
                    }, 500);
                    setAlert({ type: 'success', message: 'Profile picture updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                }}
            />

            {/* ══ 2. SECONDARY BAR ════════════════════════════════════════════════ */}
            <div className="assets-secondary-bar">
                <div className="assets-title-wrap">
                    <div className="assets-plus-icon">
                        <Plus size={13} />
                    </div>
                    <span className="assets-page-title">POLE DESIGN</span>
                </div>
                <div className="assets-top-nav">
                    {/* Navigation buttons commented out as requested - preserved for future use
                    {['Home', 'Dashboard', 'Analytics', 'Export Reports', 'Asset', 'Supplier', 'Pole Design', 'Maintenance']
                        .filter((label) => {
                            if (label === 'Home') return true;
                            const role = user?.role;
                            const normRole = role ? role.replace(/[-_ ]/g, '').toLowerCase() : '';
                            if (label === 'Analytics' && normRole !== 'superadmin') return false;
                            if (normRole === 'technicalsupport') return true;
                            if (normRole === 'superadmin') return true;
                            if (normRole === 'government') return label === 'Dashboard';
                            if (normRole === 'maintenance' || normRole === 'research') {
                                return label === 'Dashboard' || label === 'Maintenance';
                            }
                            if (normRole === 'assetmanagement') {
                                return label !== 'Maintenance' && label !== 'Export Reports';
                            }
                            return true;
                        })
                        .map((label) => (
                            <button
                                key={label}
                                className={`assets-top-nav-btn${activeTopNav === label ? ' active' : ''}`}
                                disabled={label === 'Export Reports'}
                                style={label === 'Export Reports' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                onClick={() => {
                                    setActiveTopNav(label);
                                    if (label === 'Home') setView && setView('home');
                                    else if (label === 'Dashboard') setView && setView('dashboard');
                                    else if (label === 'Analytics') setView && setView('analytics');
                                    else if (label === 'Asset') setView && setView('assets-list');
                                    else if (label === 'Supplier') setView && setView('supplier-list');
                                    else if (label === 'Pole Design') setView && setView('pole-designs');
                                    else if (label === 'Maintenance') setView && setView('maintenance');
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    */}
                    <button
                        className="assets-top-nav-btn"
                        onClick={() => setView && setView('home')}
                        title="Back to Home"
                    >
                        ← Home
                    </button>
                </div>
            </div>

            {/* ══ 3. BODY ═════════════════════════════════════════════════════════ */}
            <div className="assets-body">
                {/* Sidebar */}
                <PoleDesignSidebar setView={setView} activeItem="Pole Designs" />

                {/* Main Content Area */}
                <div className="assets-main">
                    {/* Alert Banner */}
                    {alert && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`assets-alert ${alert.type}`}
                        >
                            {alert.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
                            <span className="alert-message-flex">{alert.message}</span>
                            <button className="assets-alert-close" onClick={() => setAlert(null)}>
                                ×
                            </button>
                        </motion.div>
                    )}

                    {/* ── FORM VIEW (Add / Edit Pole Design) ───────────────────── */}
                    {showForm ? (
                        <motion.div
                            key="pole-design-form"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="comp-form-card"
                        >
                            <div className="comp-form-header">
                                <h2 className="comp-form-title">
                                    {isEditMode ? 'Edit Pole Design' : 'Add New Pole Design'}
                                </h2>
                                <p className="comp-form-subtitle">
                                    {isEditMode
                                        ? 'Update the pole design specifications and details below.'
                                        : 'Define a new pole structural template for bills of materials and sourcing.'}
                                </p>
                            </div>

                            {formErrors.submit && (
                                <div className="page-alert error" style={{ marginBottom: '16px' }}>
                                    <AlertCircle size={16} />
                                    <span className="alert-message-flex">{formErrors.submit}</span>
                                </div>
                            )}

                            <form onSubmit={handleFormSubmit} noValidate>
                                {/* Pole Thumbnail File Upload */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Pole Thumbnail Image (.png / .jpg)</label>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '16px',
                                            background: 'rgba(2, 8, 19, 0.6)',
                                            padding: '12px 16px',
                                            borderRadius: '8px',
                                            border: '1px dashed rgba(8, 120, 255, 0.3)',
                                        }}
                                    >
                                        {/* Thumbnail Preview */}
                                        <div
                                            style={{
                                                width: '56px',
                                                minHeight: '84px',
                                                maxHeight: '88px',
                                                background: 'rgba(0, 188, 255, 0.05)',
                                                border: '1px solid rgba(0, 188, 255, 0.2)',
                                                borderRadius: '6px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                overflow: 'hidden',
                                                flexShrink: 0,
                                                padding: '2px',
                                            }}
                                        >
                                            <PoleThumbnail
                                                code={formData.code || 'PD-0001'}
                                                thumbnail={formData.thumbnail}
                                            />
                                        </div>

                                        {/* Controls */}
                                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <label
                                                    htmlFor="pole-thumb-upload-input"
                                                    style={{
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: '6px',
                                                        padding: '7px 14px',
                                                        background: 'rgba(0, 188, 255, 0.12)',
                                                        border: '1px solid rgba(0, 188, 255, 0.35)',
                                                        borderRadius: '6px',
                                                        color: '#00bcff',
                                                        fontSize: '12px',
                                                        fontWeight: 600,
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                    }}
                                                >
                                                    <Upload size={13} />
                                                    {formData.thumbnail ? 'Change Image' : 'Choose Image File'}
                                                </label>
                                                <input
                                                    id="pole-thumb-upload-input"
                                                    type="file"
                                                    accept=".png,.jpg,.jpeg,image/png,image/jpeg"
                                                    style={{ display: 'none' }}
                                                    onChange={handleThumbnailFileChange}
                                                />
                                                {formData.thumbnail && (
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            setFormData((prev) => ({
                                                                ...prev,
                                                                thumbnail: '',
                                                                imageFile: null,
                                                            }))
                                                        }
                                                        style={{
                                                            padding: '7px 12px',
                                                            background: 'rgba(239, 68, 68, 0.12)',
                                                            border: '1px solid rgba(239, 68, 68, 0.3)',
                                                            borderRadius: '6px',
                                                            color: '#ef4444',
                                                            fontSize: '12px',
                                                            fontWeight: 500,
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        Remove Image
                                                    </button>
                                                )}
                                            </div>
                                            <span style={{ fontSize: '11px', color: '#64748b' }}>
                                                Upload PNG or JPG image (Max 5MB). If none is selected, default thumbnail will be used.
                                            </span>
                                        </div>
                                    </div>
                                </div>

                                {/* Pole Design Code */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Design Code *</label>
                                    <input
                                        className={`comp-form-input${formErrors.code ? ' error' : ''}`}
                                        type="text"
                                        name="code"
                                        placeholder="e.g. PD-TEST-001"
                                        value={formData.code}
                                        onChange={handleFormChange}
                                        autoFocus
                                    />
                                    {formErrors.code && <p className="site-form-error">{formErrors.code}</p>}
                                </div>

                                {/* Pole Design Name */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Pole Design Name *</label>
                                    <input
                                        className={`comp-form-input${formErrors.name ? ' error' : ''}`}
                                        type="text"
                                        name="name"
                                        placeholder="e.g. Standard AtmosPole Design"
                                        value={formData.name}
                                        onChange={handleFormChange}
                                    />
                                    <span style={{ fontSize: '11px', color: '#64748b', marginTop: '3px', display: 'block' }}>
                                        Allowed: letters, numbers, and only these special characters: @, &, -, _
                                    </span>
                                    {formErrors.name && <p className="site-form-error">{formErrors.name}</p>}
                                </div>

                                {/* Design Status */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Design Status *</label>
                                    <div className="comp-form-select-wrap">
                                        <select
                                            className={`comp-form-select${formErrors.design_status ? ' error' : ''}`}
                                            name="design_status"
                                            value={formData.design_status}
                                            onChange={handleFormChange}
                                        >
                                            {DESIGN_STATUS_OPTIONS.map((st) => (
                                                <option key={st} value={st}>
                                                    {st}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={14} className="comp-form-select-icon" />
                                    </div>
                                    {formErrors.design_status && (
                                        <p className="site-form-error">{formErrors.design_status}</p>
                                    )}
                                </div>

                                {/* Description */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Description</label>
                                    <textarea
                                        className="comp-form-textarea"
                                        name="description"
                                        placeholder="Enter standard pole design description..."
                                        value={formData.description}
                                        onChange={handleFormChange}
                                        rows={3}
                                    />
                                </div>

                                {/* Status Switch */}
                                <div className="comp-form-status-row">
                                    <span className="comp-form-status-label">Status:</span>
                                    <button
                                        type="button"
                                        className={`comp-toggle-btn ${formData.status ? 'on' : ''}`}
                                        onClick={() => setFormData((prev) => ({ ...prev, status: !prev.status }))}
                                        title={formData.status ? 'Active' : 'Inactive'}
                                    >
                                        <span className="comp-toggle-thumb" />
                                    </button>
                                    <span style={{ fontSize: '13px', color: formData.status ? '#00bcff' : '#64748b' }}>
                                        {formData.status ? 'Active' : 'Inactive'}
                                    </span>
                                </div>

                                {/* Form Actions */}
                                <div className="comp-form-actions">
                                    <button
                                        type="button"
                                        className="comp-btn-cancel"
                                        disabled={isSubmitting}
                                        onClick={() => {
                                            setShowForm(false);
                                            setEditingDesign(null);
                                            setFormData(EMPTY_FORM);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="comp-btn-save"
                                        disabled={isSubmitting}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                    >
                                        {isSubmitting && <Loader2 size={14} className="comp-spinner" />}
                                        {isEditMode ? 'Update Pole Design' : 'Save Pole Design'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    ) : (
                        /* ── TABLE VIEW (Pole Designs List) ────────────────────────── */
                        <div className="comp-card">
                            <div className="comp-header-section">
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <div>
                                        <h1 className="comp-page-title">Pole Designs</h1>
                                        <p className="comp-page-subtitle">
                                            Manage pole design structural templates, BOM configurations, and sourcing items.
                                        </p>
                                    </div>
                                    <button
                                        className="assets-action-btn"
                                        onClick={fetchPoleDesigns}
                                        title="Refresh Pole Designs"
                                        style={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px' }}
                                    >
                                        <RefreshCw size={13} className={isLoading ? 'comp-spinner' : ''} />
                                        Refresh
                                    </button>
                                </div>
                            </div>

                            {/* Toolbar */}
                            <div className="comp-toolbar">
                                <div className="comp-search-wrap">
                                    <Search size={14} color="#64748b" />
                                    <input
                                        className="comp-search-input"
                                        placeholder="Search pole designs, BOM components..."
                                        value={searchQuery}
                                        onChange={(e) => {
                                            setSearchQuery(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                                <button className="comp-add-btn" onClick={handleOpenAdd}>
                                    Add New Pole Design
                                </button>
                            </div>

                            {/* Table */}
                            <div className="comp-table-wrap">
                                <table className="comp-table">
                                    <thead>
                                        <tr>
                                            <th
                                                className="comp-th sortable comp-th-expand"
                                                onClick={() => handleSort('id')}
                                            >
                                                {/* ID */}
                                                {/* <ChevronsUpDown
                                                    size={12}
                                                    style={{
                                                        display: 'inline',
                                                        marginLeft: 4,
                                                        verticalAlign: 'middle',
                                                    }}
                                                /> */}
                                            </th>
                                            <th className="comp-th sortable" onClick={() => handleSort('code')}>
                                                Pole Design ID
                                                {sortField === 'code' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                            </th>
                                            <th className="comp-th" style={{ width: '56px', textAlign: 'center' }}>
                                                Pole
                                            </th>

                                            <th className="comp-th sortable" onClick={() => handleSort('name')}>
                                                Pole Design Name
                                                {sortField === 'name' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                            </th>
                                            <th className="comp-th sortable" onClick={() => handleSort('design_status')}>
                                                Design Status
                                                {sortField === 'design_status' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                            </th>
                                            <th className="comp-th sortable" onClick={() => handleSort('description')}>
                                                Description
                                                {sortField === 'description' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                            </th>
                                            <th
                                                className="comp-th sortable"
                                                style={{ textAlign: 'center' }}
                                                onClick={() => handleSort('components')}
                                            >
                                                No. of Components
                                                {sortField === 'components' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                            </th>
                                            <th className="comp-th comp-th-status">Status</th>
                                            <th className="comp-th comp-th-action">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            <tr>
                                                <td colSpan={9}>
                                                    <div className="comp-loading-state">
                                                        <Loader2 size={24} className="comp-spinner" />
                                                        <span>Loading pole designs...</span>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : paginated.length === 0 ? (
                                            <tr>
                                                <td colSpan={9} className="assets-td assets-table-empty">
                                                    No pole designs found.
                                                </td>
                                            </tr>
                                        ) : (
                                            paginated.map((item) => {
                                                const isExpanded = Boolean(expandedRows[item.id]);
                                                const totalCount = getComponentCount(item);
                                                const bomList = boms[item.id] || item.components || [];
                                                const isBomLoading = Boolean(loadingBoms[item.id]);

                                                return (
                                                    <React.Fragment key={item.id}>
                                                        <tr className="comp-table-row">
                                                            {/* ID + Expand Toggle */}
                                                            <td className="comp-td">
                                                                <button
                                                                    className={`comp-expand-btn${isExpanded ? ' open' : ''
                                                                        }`}
                                                                    onClick={() => toggleRowExpand(item.id)}
                                                                    title="Expand BOM Components"
                                                                >
                                                                    <ChevronRight size={14} />
                                                                </button>
                                                                {/* <span>{item.id}</span> */}
                                                            </td>

                                                            {/* Pole Thumbnail */}


                                                            {/* Design Code */}
                                                            <td className="comp-td">
                                                                <span className="comp-code-pill">{item.code}</span>
                                                            </td>
                                                            <td
                                                                className="comp-td"
                                                                style={{ textAlign: 'center', padding: '6px 8px' }}
                                                            >
                                                                <PoleThumbnail
                                                                    code={item.code}
                                                                    id={item.id}
                                                                    thumbnail={item.image}
                                                                />
                                                            </td>

                                                            {/* Pole Design Name */}
                                                            <td
                                                                className="comp-td"
                                                                style={{ fontWeight: 600, color: '#f1f5f9' }}
                                                            >
                                                                {item.name}
                                                            </td>

                                                            {/* Design Status */}
                                                            <td className="comp-td">
                                                                <span
                                                                    className={`comp-status-badge ${getStatusClass(
                                                                        item.design_status
                                                                    )}`}
                                                                >
                                                                    {item.design_status || 'Concept'}
                                                                </span>
                                                            </td>

                                                            {/* Description */}
                                                            <td
                                                                className="comp-td"
                                                                style={{
                                                                    color: '#94a3b8',
                                                                    fontSize: '12.5px',
                                                                    maxWidth: '300px',
                                                                    whiteSpace: 'normal',
                                                                    lineHeight: '1.4',
                                                                }}
                                                            >
                                                                {item.description || '—'}
                                                            </td>

                                                            {/* No. of Components */}
                                                            <td
                                                                className="comp-td"
                                                                style={{
                                                                    textAlign: 'center',
                                                                    fontWeight: 600,
                                                                    color: '#00bcff',
                                                                }}
                                                            >
                                                                {isBomLoading ? (
                                                                    <Loader2
                                                                        size={12}
                                                                        className="comp-spinner"
                                                                        style={{ display: 'inline' }}
                                                                    />
                                                                ) : (
                                                                    totalCount
                                                                )}
                                                            </td>

                                                            {/* Status Switch */}
                                                            <td className="comp-td comp-td-status">
                                                                <button
                                                                    className={`comp-toggle-btn ${item.status ? 'on' : ''
                                                                        }`}
                                                                    onClick={(e) => handleToggleStatus(item.id, e)}
                                                                    title={item.status ? 'Active' : 'Inactive'}
                                                                >
                                                                    <span className="comp-toggle-thumb" />
                                                                </button>
                                                            </td>

                                                            {/* Actions */}
                                                            <td className="comp-td comp-td-action">
                                                                <span className="assets-action-group">
                                                                    <button
                                                                        className="assets-action-btn edit"
                                                                        title="Edit Pole Design"
                                                                        onClick={() => handleOpenEdit(item)}
                                                                    >
                                                                        <Edit2 size={14} />
                                                                    </button>
                                                                    <button
                                                                        className="assets-action-btn delete"
                                                                        title="Delete Pole Design"
                                                                        onClick={() => setPendingDelete(item)}
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </span>
                                                            </td>
                                                        </tr>

                                                        {/* ── Expanded BOM Details Row ─── */}
                                                        {isExpanded && (
                                                            <tr className="comp-detail-row">
                                                                <td colSpan={9} style={{ padding: '16px 20px' }}>
                                                                    <div className="bom-sub-card">
                                                                        {/* Header */}
                                                                        <div className="bom-sub-header">
                                                                            <span className="bom-sub-title">
                                                                                BOM Details [{item.name} / {item.code}]
                                                                            </span>
                                                                            <button
                                                                                type="button"
                                                                                className="bom-add-btn"
                                                                                onClick={() => handleOpenAddBOMItem(item)}
                                                                            >
                                                                                <Plus size={13} />
                                                                                Add New Component
                                                                            </button>
                                                                        </div>

                                                                        {/* BOM Table */}
                                                                        {isBomLoading ? (
                                                                            <div
                                                                                style={{
                                                                                    padding: '24px',
                                                                                    textAlign: 'center',
                                                                                    color: '#94a3b8',
                                                                                    fontSize: '13px',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center',
                                                                                    gap: '8px',
                                                                                }}
                                                                            >
                                                                                <Loader2 size={16} className="comp-spinner" />
                                                                                <span>Loading BOM components...</span>
                                                                            </div>
                                                                        ) : bomList.length === 0 ? (
                                                                            <div
                                                                                style={{
                                                                                    padding: '24px',
                                                                                    textAlign: 'center',
                                                                                    color: '#64748b',
                                                                                    fontSize: '13px',
                                                                                }}
                                                                            >
                                                                                No BOM components defined for {item.name}. Click "Add New Component" to configure bill of materials.
                                                                            </div>
                                                                        ) : (
                                                                            <table className="bom-table">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th className="bom-th">Component Code</th>
                                                                                        <th className="bom-th">Component Name</th>
                                                                                        <th className="bom-th">Category</th>
                                                                                        <th className="bom-th" style={{ textAlign: 'center' }}>Qty</th>
                                                                                        <th className="bom-th" style={{ textAlign: 'center' }}>Unit of Measure</th>
                                                                                        <th className="bom-th">Notes</th>
                                                                                        <th className="bom-th" style={{ textAlign: 'center' }}>Action</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    {bomList.map((c) => (
                                                                                        <tr key={c.bom_item_id || c.code}>
                                                                                            <td className="bom-td">
                                                                                                <span className="comp-code-pill">
                                                                                                    {c.code}
                                                                                                </span>
                                                                                            </td>
                                                                                            <td
                                                                                                className="bom-td"
                                                                                                style={{
                                                                                                    color: '#f1f5f9',
                                                                                                    fontWeight: 500,
                                                                                                }}
                                                                                            >
                                                                                                {c.name}
                                                                                            </td>
                                                                                            <td className="bom-td">
                                                                                                <span className="comp-badge-cat">
                                                                                                    {c.category || 'General'}
                                                                                                </span>
                                                                                            </td>
                                                                                            <td
                                                                                                className="bom-td"
                                                                                                style={{
                                                                                                    textAlign: 'center',
                                                                                                    fontWeight: 600,
                                                                                                    color: '#00bcff',
                                                                                                }}
                                                                                            >
                                                                                                {c.quantity}
                                                                                            </td>
                                                                                            <td
                                                                                                className="bom-td"
                                                                                                style={{
                                                                                                    textAlign: 'center',
                                                                                                    color: '#94a3b8',
                                                                                                }}
                                                                                            >
                                                                                                {c.unitOfMeasure || 'pcs'}
                                                                                            </td>
                                                                                            <td
                                                                                                className="bom-td"
                                                                                                style={{
                                                                                                    color: '#94a3b8',
                                                                                                    fontSize: '12px',
                                                                                                    maxWidth: '220px',
                                                                                                }}
                                                                                            >
                                                                                                {c.notes || '—'}
                                                                                            </td>
                                                                                            <td
                                                                                                className="bom-td"
                                                                                                style={{ textAlign: 'center' }}
                                                                                            >
                                                                                                <span className="assets-action-group">
                                                                                                    <button
                                                                                                        className="assets-action-btn edit"
                                                                                                        title="Edit Component"
                                                                                                        onClick={() => handleOpenEditBOMItem(item, c)}
                                                                                                    >
                                                                                                        <Edit2 size={13} />
                                                                                                    </button>
                                                                                                    <button
                                                                                                        className="assets-action-btn delete"
                                                                                                        title="Remove Component from BOM"
                                                                                                        onClick={() =>
                                                                                                            setPendingDeleteBOM({
                                                                                                                poleId: item.id,
                                                                                                                bomItemId: c.bom_item_id,
                                                                                                                compName: c.name || c.code,
                                                                                                            })
                                                                                                        }
                                                                                                    >
                                                                                                        <Trash2 size={13} />
                                                                                                    </button>
                                                                                                </span>
                                                                                            </td>
                                                                                        </tr>
                                                                                    ))}
                                                                                </tbody>
                                                                            </table>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Footer / Pagination */}
                            <div className="comp-footer">
                                <div className="comp-showing-text">
                                    Showing {sorted.length > 0 ? (currentPage - 1) * ROWS_PER_PAGE + 1 : 0} to{' '}
                                    {Math.min(currentPage * ROWS_PER_PAGE, sorted.length)} of {sorted.length} pole designs
                                </div>

                                <div className="comp-pagination">
                                    <button
                                        className="comp-page-btn"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    >
                                        &lt;
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                        <button
                                            key={page}
                                            className={`comp-page-btn${currentPage === page ? ' active' : ''}`}
                                            onClick={() => setCurrentPage(page)}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                    <button
                                        className="comp-page-btn"
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    >
                                        &gt;
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── BOM Drawer / Modal (Add / Edit Component to BOM) ────────────────── */}
            <AnimatePresence>
                {bomModal.isOpen && (
                    <div
                        className="bom-modal-backdrop"
                        onClick={() =>
                            setBomModal({
                                isOpen: false,
                                poleId: null,
                                poleName: '',
                                poleCode: '',
                                isEdit: false,
                                bomItemId: null,
                            })
                        }
                    >
                        <motion.div
                            initial={{ opacity: 0, x: 100 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 100 }}
                            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                            className="bom-modal-panel"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Header */}
                            <div className="bom-modal-header">
                                <div>
                                    <h3 className="bom-modal-title">
                                        {bomModal.isEdit ? 'Edit BOM Component' : 'Add Component to BOM'}
                                    </h3>
                                    <p className="bom-modal-subtitle">
                                        Assign and configure a design component within this pole structure.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    className="bom-modal-close"
                                    onClick={() =>
                                        setBomModal({
                                            isOpen: false,
                                            poleId: null,
                                            poleName: '',
                                            poleCode: '',
                                            isEdit: false,
                                            bomItemId: null,
                                        })
                                    }
                                >
                                    <X size={18} />
                                </button>
                            </div>

                            {bomFormErrors.submit && (
                                <div className="page-alert error" style={{ marginBottom: '12px' }}>
                                    <AlertCircle size={16} />
                                    <span className="alert-message-flex">{bomFormErrors.submit}</span>
                                </div>
                            )}

                            <form
                                onSubmit={handleSaveBOMItem}
                                noValidate
                                style={{ display: 'flex', flexDirection: 'column', gap: '14px', flex: 1 }}
                            >
                                {/* Pole Design (Readonly) */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Pole Design</label>
                                    <input
                                        className="comp-form-input"
                                        type="text"
                                        value={bomModal.poleName}
                                        disabled
                                        style={{ opacity: 0.8, cursor: 'not-allowed', background: '#020914' }}
                                    />
                                </div>

                                {/* Design Code (Readonly) */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Design Code</label>
                                    <input
                                        className="comp-form-input"
                                        type="text"
                                        value={bomModal.poleCode}
                                        disabled
                                        style={{ opacity: 0.8, cursor: 'not-allowed', background: '#020914' }}
                                    />
                                </div>

                                {/* Component Dropdown */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Component *</label>
                                    <div className="comp-form-select-wrap">
                                        <select
                                            className={`comp-form-select${bomFormErrors.component ? ' error' : ''}`}
                                            value={bomFormData.component_id}
                                            onChange={(e) => handleBOMComponentSelect(e.target.value)}
                                            disabled={bomModal.isEdit}
                                        >
                                            <option value="">Select Component</option>
                                            {availableComponents.map((c) => {
                                                const isAlreadyInBOM =
                                                    !bomModal.isEdit &&
                                                    boms[bomModal.poleId]?.some(
                                                        (b) => String(b.component_id) === String(c.id)
                                                    );
                                                return (
                                                    <option key={c.id || c.code} value={c.id}>
                                                        {c.code} - {c.name}
                                                        {isAlreadyInBOM ? ' (Already in BOM)' : ''}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                        <ChevronDown size={14} className="comp-form-select-icon" />
                                    </div>
                                    {bomFormErrors.component && (
                                        <p className="site-form-error">{bomFormErrors.component}</p>
                                    )}
                                </div>

                                {/* Component Code */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Component Code</label>
                                    <input
                                        className="comp-form-input"
                                        type="text"
                                        value={bomFormData.code}
                                        placeholder="Component Code"
                                        readOnly
                                        style={{ background: '#020914' }}
                                    />
                                </div>

                                {/* Component Name */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Component Name</label>
                                    <input
                                        className="comp-form-input"
                                        type="text"
                                        value={bomFormData.name}
                                        placeholder="Component Name"
                                        readOnly
                                        style={{ background: '#020914' }}
                                    />
                                </div>

                                {/* Category */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Category</label>
                                    <input
                                        className="comp-form-input"
                                        type="text"
                                        value={bomFormData.category}
                                        placeholder="Category"
                                        readOnly
                                        style={{ background: '#020914' }}
                                    />
                                </div>

                                {/* Unit of Measure */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Unit of Measure</label>
                                    <input
                                        className="comp-form-input"
                                        type="text"
                                        value={bomFormData.unitOfMeasure}
                                        readOnly
                                        style={{ background: '#020914' }}
                                    />
                                </div>

                                {/* Quantity */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Quantity *</label>
                                    <input
                                        className={`comp-form-input${bomFormErrors.quantity ? ' error' : ''}`}
                                        type="number"
                                        min="1"
                                        value={bomFormData.quantity}
                                        onChange={(e) =>
                                            setBomFormData((prev) => ({
                                                ...prev,
                                                quantity: Math.max(1, parseInt(e.target.value, 10) || 1),
                                            }))
                                        }
                                        placeholder="Quantity"
                                    />
                                    {bomFormErrors.quantity && (
                                        <p className="site-form-error">{bomFormErrors.quantity}</p>
                                    )}
                                </div>

                                {/* Notes */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Notes</label>
                                    <textarea
                                        className="comp-form-textarea"
                                        value={bomFormData.notes}
                                        onChange={(e) => setBomFormData((prev) => ({ ...prev, notes: e.target.value }))}
                                        placeholder="e.g. Main air cleaning fan..."
                                        rows={3}
                                    />
                                </div>

                                {/* Form Actions */}
                                <div
                                    className="comp-form-actions"
                                    style={{ marginTop: 'auto', paddingTop: '16px' }}
                                >
                                    <button
                                        type="button"
                                        className="comp-btn-cancel"
                                        disabled={isSubmittingBOM}
                                        onClick={() =>
                                            setBomModal({
                                                isOpen: false,
                                                poleId: null,
                                                poleName: '',
                                                poleCode: '',
                                                isEdit: false,
                                                bomItemId: null,
                                            })
                                        }
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="comp-btn-save"
                                        disabled={isSubmittingBOM}
                                        style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                                    >
                                        {isSubmittingBOM && <Loader2 size={14} className="comp-spinner" />}
                                        {bomModal.isEdit ? 'Update BOM Item' : 'Save BOM Item'}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>

            {/* ── Confirm Delete Pole Design Modal ──────────────────────────── */}
            <AnimatePresence>
                {pendingDelete && (
                    <ConfirmDeleteModal
                        message={`Are you sure you want to delete pole design "${pendingDelete.name || pendingDelete.code || pendingDelete
                            }"? This action cannot be undone.`}
                        isLoading={isDeleting}
                        onConfirm={handleConfirmDelete}
                        onCancel={() => setPendingDelete(null)}
                    />
                )}
            </AnimatePresence>

            {/* ── Confirm Delete BOM Item Modal ─────────────────────────────── */}
            <AnimatePresence>
                {pendingDeleteBOM && (
                    <ConfirmDeleteModal
                        message={`Are you sure you want to remove "${pendingDeleteBOM.compName}" from the BOM?`}
                        isLoading={isDeleting}
                        onConfirm={handleConfirmDeleteBOM}
                        onCancel={() => setPendingDeleteBOM(null)}
                    />
                )}
            </AnimatePresence>

            {/* ══ 4. FOOTER ═══════════════════════════════════════════════════════ */}
            <InnerFooter />
        </motion.div>
    );
};

export default PoleDesigns;
